import React from "react";
import { Info, Paperclip, Upload, X } from "lucide-react";
import { toast } from "../../utils/toast";
import translations from "./Translations";
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_SIZE_MB,
  maxAttachmentsMessage,
  mergeAttachments,
} from "../../utils/attachments";

// Multi-file version of AttachProofInput, used where the backend takes the
// files themselves as repeated `attachments` multipart parts (RC create,
// bulk RC, instalment plan). Markup matches AttachProofInput so the control
// looks the same on every screen.
const MultiAttachInput = ({ files, onChange, theme, langMode, showNote = true }) => {
  const isRtl = langMode === "ar" || langMode === "ur";
  const isFull = files.length >= MAX_ATTACHMENTS;

  const handleFileChange = (e) => {
    const { files: next, error } = mergeAttachments(files, e.target.files, langMode);
    e.target.value = ""; // lets the same file be picked again after removal
    if (error) {
      toast.error(error);
      return;
    }
    onChange(next);
  };

  const removeFile = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const pickerLabel =
    files.length === 0
      ? translations[langMode]?.noFile ?? "No file chosen"
      : `${files.length} / ${MAX_ATTACHMENTS} ${translations[langMode]?.filesSelected ?? "files selected"}`;

  return (
    <div className="flex flex-col relative">
      <label className="text-sm font-semibold mb-1 ml-2">
        {translations[langMode].attachProof || "Attach Proof (PDF/Image)"}
      </label>
      <div
        className={`relative w-full min-h-[50px] flex items-center overflow-hidden rounded-lg border px-3 transition-colors focus-within:border-blue-500 focus-within:outline-none ${
          theme === "dark"
            ? "border-gray-600 bg-[#0f172a]"
            : theme === "high-contrast"
              ? "border-black bg-white"
              : "border-gray-300 bg-white"
        } ${isFull ? "opacity-60" : ""}`}
      >
        {/* Full selection: clicking the picker explains the cap instead of
            opening a dialog whose 6th file would only be rejected. */}
        {isFull ? (
          <button
            type="button"
            onClick={() => toast.error(maxAttachmentsMessage(langMode))}
            className="absolute inset-0 z-20 h-full w-full cursor-not-allowed opacity-0"
            aria-label={translations[langMode]?.chooseFile ?? "Choose File"}
          />
        ) : (
          <input
            type="file"
            multiple
            accept="application/pdf,image/*"
            onChange={handleFileChange}
            className="absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0"
          />
        )}
        <div
          className={`relative z-10 flex w-full items-center justify-between gap-3 ${
            isRtl ? "flex-row-reverse text-right" : ""
          }`}
        >
          <div className={`flex min-w-0 items-center gap-3 ${isRtl ? "flex-row-reverse" : ""}`}>
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#236DB4]/10 text-[#236DB4]">
              <Upload size={15} />
            </span>
            <span className="inline-flex shrink-0 rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-semibold text-white sm:text-sm">
              {translations[langMode]?.chooseFile ?? "Choose File"}
            </span>
            <span
              className={`truncate text-xs sm:text-sm ${
                theme === "dark" ? "text-gray-200" : "text-gray-600"
              }`}
            >
              {pickerLabel}
            </span>
          </div>
          <div className={`shrink-0 text-gray-400 ${theme === "dark" ? "text-gray-300" : ""}`}>
            <Paperclip size={16} />
          </div>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500 inline-flex items-center gap-1.5">
        <Upload size={12} />
        PDF, JPG, JPEG, PNG · {MAX_ATTACHMENTS} × {MAX_ATTACHMENT_SIZE_MB} MB
      </p>
      {showNote && (
        <p className="mt-1 flex items-start gap-1.5 text-xs italic text-gray-500">
          <Info size={12} className="mt-[2px] shrink-0" />
          <span>
            {translations[langMode].attachProofNote ||
              "Note: The uploaded document is stored against this account and will be available to view later."}
          </span>
        </p>
      )}
      {files.length > 0 && (
        <ul className="mt-1 flex flex-col gap-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.lastModified}-${index}`}
              className="flex items-center gap-1.5 text-xs text-green-600"
            >
              <Paperclip size={12} className="shrink-0" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => removeFile(index)}
                aria-label={translations[langMode]?.removeAttachment ?? "Remove attachment"}
                className="inline-flex shrink-0 items-center justify-center text-gray-400 hover:text-red-500"
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MultiAttachInput;
